import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import api from "@/lib/api";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const email = searchParams.get("email");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  // Validate password strength
  const validatePassword = (pass) => {
    if (pass.length < 8) return "Password must be at least 8 characters long.";
    if (!/[A-Z]/.test(pass)) return "Password must contain an uppercase letter.";
    if (!/[a-z]/.test(pass)) return "Password must contain a lowercase letter.";
    if (!/[0-9]/.test(pass)) return "Password must contain a digit.";
    if (!/[^A-Za-z0-9]/.test(pass)) return "Password must contain a special character.";
    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      setLoading(false);
      return;
    }

    const passError = validatePassword(password);
    if (passError) {
      setError(passError);
      setLoading(false);
      return;
    }

    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid or expired reset link. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-zinc-50">
        <div className="w-full max-w-sm space-y-6 bg-white p-8 rounded-xl shadow-sm border border-border text-center">
          <h2 className="font-heading font-black text-2xl tracking-tighter">Password Reset Complete</h2>
          <p className="text-sm text-zinc-500">Your password has been successfully reset.</p>
          <Button asChild className="w-full bg-zinc-900 text-white hover:bg-zinc-800">
            <Link to="/login">Go to Login</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-zinc-50">
      <div className="w-full max-w-sm space-y-6 bg-white p-8 rounded-xl shadow-sm border border-border">
        <div>
          <div className="overline text-zinc-500 mb-2">Security</div>
          <h2 className="font-heading font-black text-3xl tracking-tighter">Set New Password</h2>
          <p className="text-sm text-zinc-500 mt-1">
            {email ? `For ${email}` : "Create a new strong password."}
          </p>
        </div>

        <form onSubmit={onSubmit} className="space-y-6" data-testid="reset-password-form">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">New Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="reset-new-password"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                data-testid="reset-confirm-password"
              />
            </div>
          </div>

          {error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-md">
              {error}
            </div>
          )}

          <Button
            type="submit"
            className="w-full bg-zinc-900 text-white hover:bg-zinc-800 h-11"
            disabled={loading || !token}
            data-testid="reset-submit"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Reset Password
          </Button>
        </form>
      </div>
    </div>
  );
}
